import { connectionApi } from '../../lib/connections-api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { request } from '../../lib/api-client';
import { ConnectionOperationCard } from './ConnectionOperationCard';
import styles from './Connections.module.css';
interface Plan {
  id: string;
  connection_id: string;
  status: string;
  revision: number;
  request_hash: string;
  steps: Array<{ title: string; arguments: unknown }>;
  operations: string[];
  safe_error: string | null;
}
export function ConnectionPlanCard({ planId }: { planId: string }) {
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  const query = useQuery({
    queryKey: ['connection-plan', scope, planId],
    queryFn: () => request<Plan>(`/v1/connection-plans/${planId}`),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 5000 : false),
  });
  const connection = useQuery({
    queryKey: ['connection', scope, query.data?.connection_id],
    queryFn: () => connectionApi.detail(query.data!.connection_id),
    enabled: !!query.data?.connection_id,
  });
  const command = useMutation({
    mutationFn: (action: string) =>
      request<Plan>(`/v1/connection-plans/${planId}/${action}`, {
        method: 'POST',
        body:
          action === 'approve'
            ? {
                expectedRevision: query.data!.revision,
                requestHash: query.data!.request_hash,
              }
            : action === 'cancel'
              ? { expectedRevision: query.data!.revision }
              : {},
      }),
    onSuccess: () => void query.refetch(),
  });
  if (!query.data)
    return (
      <p role="status">
        {query.isError
          ? 'Connection plan unavailable.'
          : 'Loading reviewed plan…'}
      </p>
    );
  const plan = query.data;
  return (
    <section className={styles.card}>
      <strong>
        {connection.data?.name ?? 'Connection'} plan · {plan.status}
      </strong>
      {connection.data?.endpoints.map((endpoint) => (
        <p key={endpoint.id} className={styles.muted}>
          {endpoint.url} ·{' '}
          {JSON.stringify(
            endpoint.metadata.identity ?? 'Account/environment not supplied',
          )}
        </p>
      ))}
      <p className={styles.muted}>
        Approval covers these exact steps and only the bounded result
        identifiers shown. Completed steps remain saved if a later step fails.
        External charges may apply.
      </p>
      {plan.steps.map((step, index) => (
        <div key={index}>
          <strong>
            {index + 1}. {step.title}
          </strong>
          <pre className={styles.result}>
            {JSON.stringify(step.arguments, null, 2)}
          </pre>
        </div>
      ))}
      {plan.status === 'awaiting_approval' && (
        <button
          disabled={command.isPending}
          onClick={() => command.mutate('approve')}
        >
          Approve this plan
        </button>
      )}
      {plan.status === 'running' && (
        <button
          disabled={command.isPending}
          onClick={() => command.mutate('continue')}
        >
          Continue from known results
        </button>
      )}
      {['running', 'awaiting_approval'].includes(plan.status) && (
        <button
          disabled={command.isPending}
          onClick={() => command.mutate('cancel')}
        >
          Cancel remaining steps
        </button>
      )}
      {plan.safe_error && <p role="status">{plan.safe_error}</p>}
      {plan.operations.map((id) => (
        <ConnectionOperationCard key={id} operationId={id} />
      ))}
      {command.isError && <p role="alert">{command.error.message}</p>}
    </section>
  );
}
export function ConnectionPlans({ connectionId }: { connectionId: string }) {
  const { bootstrap } = useAuth();
  const query = useQuery({
    queryKey: [
      'connection-plans',
      bootstrap?.workspace.id,
      bootstrap?.user?.id,
      connectionId,
    ],
    queryFn: () =>
      request<{ items: Plan[] }>(`/v1/connections/${connectionId}/plans`),
    refetchInterval: 10000,
  });
  return (
    <>
      {query.data?.items.map((plan) => (
        <ConnectionPlanCard key={plan.id} planId={plan.id} />
      ))}
    </>
  );
}
